import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  X, ChevronLeft, ChevronRight, Send, RefreshCw, CheckCircle, AlertCircle,
  Clock, HelpCircle, FileText, AlertTriangle, MessageSquare
} from 'lucide-react';
import ConversationMode from './ConversationMode';
import { useDynamicForm } from './dynamicform/useDynamicForm';
import DynamicFormField from './dynamicform/DynamicFormField';

/**
 * DynamicFormModal - Affiche un formulaire dynamique depuis le Form Builder
 */
const DynamicFormModal = ({ slug, onClose, backendUrl, initialData = {}, clientId = null, courtierId = null, authToken = null, embedded = false, hideClientFields = true }) => {
  const { t, i18n } = useTranslation('client');
  const form = useDynamicForm({ slug, backendUrl, initialData, clientId, courtierId, authToken, hideClientFields, t, i18n });
  const {
    formTemplate, loading, error, formLang, getTranslatedText,
    currentCardIndex, setCurrentCardIndex, setNavigationHistory,
    formData, uploadedFiles, submitting, submitted, showRecap, setShowRecap,
    isAnimating, animationDirection,
    useConversationMode, setUseConversationMode, conversationDataRef,
    switchToClassicMode, handleConversationSuccess, shouldHideField,
    calculateRemainingTime, formatRemainingTime,
    handleNext, handlePrevious, handleSubmit, isCurrentCardValid
  } = form;

  // Options UX du formulaire
  const showProgressInfo = formTemplate?.styles?.showProgressInfo !== false;
  const enableAnimations = formTemplate?.styles?.enableAnimations !== false;
  const showImportantBadge = formTemplate?.styles?.showImportantBadge !== false;
  const showRecapBeforeSend = formTemplate?.styles?.showRecapBeforeSend !== false;

  const customStyles = {
    fontFamily: formTemplate?.styles?.fontFamily || 'inherit',
    headingColor: formTemplate?.styles?.headingColor || '#1e293b',
    headingSize: formTemplate?.styles?.headingSize || 'text-xl',
    headingWeight: formTemplate?.styles?.headingWeight || 'font-bold',
    labelColor: formTemplate?.styles?.labelColor || '#374151',
    labelSize: formTemplate?.styles?.labelSize || 'text-sm',
    labelWeight: formTemplate?.styles?.labelWeight || 'font-semibold',
    accentColor: formTemplate?.styles?.accentColor || '#0891b2'
  };

  const trustMessage = formTemplate?.styles?.trustMessage || (formLang === 'en'
    ? '🔒 Your data is secure and confidential. We will respond within 24 hours.'
    : '🔒 Vos données sont sécurisées et confidentielles. Nous vous répondrons sous 24h.');
  const customSuccessTitle = formTemplate?.styles?.successTitle || (formLang === 'en' ? 'Request sent!' : 'Demande envoyée !');
  const customSuccessMessage = formTemplate?.styles?.successMessage || (formLang === 'en'
    ? 'Your request has been sent! We will contact you as soon as possible.'
    : 'Votre demande a bien été envoyée ! Nous vous recontacterons dans les plus brefs délais.');

  const aiConversationEnabled = formTemplate?.styles?.aiConversationEnabled || false;

  // Rendu loading
  if (loading) {
    if (embedded) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="animate-spin text-cyan-600" size={48} />
        </div>
      );
    }
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8">
          <RefreshCw className="animate-spin text-cyan-600 mx-auto" size={48} />
        </div>
      </div>
    );
  }

  // Rendu mode conversation IA
  if (useConversationMode && aiConversationEnabled) {
    const conversationContent = (
      <ConversationMode
        formSlug={slug}
        backendUrl={backendUrl}
        onSwitchToClassic={switchToClassicMode}
        onSubmitSuccess={handleConversationSuccess}
        collectedDataRef={conversationDataRef}
        formName={getTranslatedText(formTemplate?.name, formTemplate?.name_en)}
      />
    );

    if (embedded) {
      return (
        <div className="border border-slate-200 rounded-xl overflow-hidden h-[500px]">
          {conversationContent}
        </div>
      );
    }
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg h-[600px] flex flex-col overflow-hidden relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 hover:bg-slate-100 rounded-lg z-10 bg-white/80"
          >
            <X size={18} />
          </button>
          {conversationContent}
        </div>
      </div>
    );
  }

  // Rendu erreur (formulaire non configuré)
  if (error || !formTemplate?.cards?.length) {
    if (embedded) {
      return (
        <div className="text-center py-8">
          <AlertCircle className="mx-auto mb-4 text-amber-500" size={48} />
          <h2 className="text-xl font-bold text-slate-800 mb-2">{t('form.configuring')}</h2>
          <p className="text-slate-600">{t('form.notAvailable')}</p>
        </div>
      );
    }
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md text-center">
          <AlertCircle className="mx-auto mb-4 text-amber-500" size={48} />
          <h2 className="text-xl font-bold text-slate-800 mb-2">{t('form.configuring')}</h2>
          <p className="text-slate-600 mb-6">{t('form.notAvailable')}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
            >
              {t('form.close')}
            </button>
            <a
              href="/contact"
              className="px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
            >
              {t('form.contactUs')}
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Rendu succès avec messages personnalisés
  if (submitted) {
    if (embedded) {
      return (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-green-600" size={32} />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">{customSuccessTitle}</h2>
          <p className="text-slate-600">{customSuccessMessage}</p>
        </div>
      );
    }
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-green-600" size={32} />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">{customSuccessTitle}</h2>
          <p className="text-slate-600 mb-6">{customSuccessMessage}</p>
          <button
            onClick={onClose}
            className="px-8 py-3 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700 font-medium"
          >
            {t('form.close')}
          </button>
        </div>
      </div>
    );
  }

  // Rendu du récapitulatif avant envoi
  if (showRecap) {
    const getAnswers = () => {
      const answers = [];
      formTemplate?.cards?.forEach((card, cardIndex) => {
        card.fields?.forEach(field => {
          if (field.type === 'heading') return;
          const value = formData[field.id];
          const files = uploadedFiles[field.id];
          if (value || (files && files.length > 0)) {
            let displayValue = value;
            if (field.type === 'file' && files && files.length > 0) {
              displayValue = files.map(f => f.name).join(', ');
            } else if (Array.isArray(value)) {
              displayValue = value.join(', ');
            } else if (field.type === 'date' && value) {
              displayValue = new Date(value).toLocaleDateString(formLang === 'en' ? 'en-US' : 'fr-FR');
            }
            if (displayValue) {
              answers.push({
                cardIndex,
                cardTitle: getTranslatedText(card.title, card.title_en),
                fieldId: field.id,
                label: getTranslatedText(field.label, field.label_en),
                value: displayValue,
                type: field.type
              });
            }
          }
        });
      });
      return answers;
    };

    const answers = getAnswers();

    const recapContent = (
      <div className="flex flex-col flex-1 min-h-0">
        <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white p-5 relative shrink-0">
          <h2 className="text-xl font-bold">
            {formLang === 'en' ? '📋 Summary of your answers' : '📋 Récapitulatif de vos réponses'}
          </h2>
          <p className="text-cyan-100 text-sm mt-1">
            {formLang === 'en' ? 'Please check your answers before sending' : 'Vérifiez vos réponses avant d\'envoyer'}
          </p>
          {/* Bouton fermer — Bug 3 fix */}
          <button
            onClick={() => setShowRecap(false)}
            className="absolute top-4 right-4 p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            title={formLang === 'en' ? 'Close' : 'Fermer'}
          >
            <X size={18} className="text-white" />
          </button>
        </div>

        {/* Bug 2 fix : overflow-y scroll + webkit-overflow-scrolling pour Safari */}
        <div
          className="flex-1 min-h-0 p-5"
          style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}
        >
          <div className="space-y-3">
            {answers.map((answer, idx) => (
              <div
                key={idx}
                className="p-3 bg-slate-50 rounded-lg border border-slate-200 group hover:border-cyan-300 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-500 mb-1">{answer.cardTitle}</p>
                    <p className="text-sm font-medium text-slate-700">{answer.label}</p>
                    <p className="text-sm text-slate-900 mt-1 break-words">
                      {answer.type === 'file' ? (
                        <span className="flex items-center gap-1 text-cyan-600">
                          <FileText size={14} />
                          {answer.value}
                        </span>
                      ) : (
                        answer.value
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowRecap(false);
                      setCurrentCardIndex(answer.cardIndex);
                      setNavigationHistory([...Array(answer.cardIndex + 1).keys()]);
                    }}
                    className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded opacity-0 group-hover:opacity-100 transition-all"
                    title={formLang === 'en' ? 'Edit' : 'Modifier'}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <p className="text-sm text-emerald-700">{trustMessage}</p>
          </div>
        </div>

        <div className="p-4 border-t border-slate-100 flex justify-between items-center bg-slate-50 shrink-0">
          <button
            onClick={() => setShowRecap(false)}
            className="px-4 py-2 text-slate-600 hover:bg-white rounded-lg flex items-center gap-2"
          >
            <ChevronLeft size={18} />
            {formLang === 'en' ? 'Back' : 'Retour'}
          </button>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white rounded-xl hover:from-cyan-600 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
          >
            {submitting ? (
              <>
                <RefreshCw className="animate-spin" size={18} />
                {t('form.sending')}
              </>
            ) : (
              <>
                <Send size={18} />
                {formLang === 'en' ? 'Confirm and send' : 'Confirmer et envoyer'}
              </>
            )}
          </button>
        </div>
      </div>
    );

    if (embedded) {
      return (
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          {recapContent}
        </div>
      );
    }
    return (
      /* Bug 2 fix : suppression de overflow-hidden sur le conteneur externe */
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" style={{ overflow: 'hidden' }}>
          {recapContent}
        </div>
      </div>
    );
  }

  const currentCard = formTemplate?.cards?.[currentCardIndex];
  const isLastCard = currentCardIndex === (formTemplate?.cards?.length || 1) - 1;
  const totalCards = formTemplate?.cards?.length || 1;
  const remainingTime = calculateRemainingTime();

  const getAnimationClasses = () => {
    if (!enableAnimations) return '';
    if (isAnimating) {
      return animationDirection === 'next'
        ? 'opacity-0 translate-x-4'
        : 'opacity-0 -translate-x-4';
    }
    return 'opacity-100 translate-x-0';
  };

  const formContent = (
    <>
      <div
        className={`${embedded ? 'rounded-t-2xl' : ''} relative`}
        style={{
          background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 50%, #22d3ee 100%)',
          padding: embedded ? '1.25rem' : '1.5rem'
        }}
      >
        <div className="absolute inset-0 opacity-10 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
        </div>

        {!embedded && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-xl transition-colors z-20"
            style={{ backdropFilter: 'blur(4px)' }}
            data-testid="close-form-modal"
          >
            <X size={20} className="text-white" />
          </button>
        )}

        <div className="relative z-10">
          <h2 className={`${embedded ? 'text-lg' : 'text-2xl'} font-bold text-white ${!embedded ? 'pr-10' : ''}`}>
            {getTranslatedText(formTemplate?.name, formTemplate?.name_en)}
          </h2>
          {(formTemplate?.description || formTemplate?.description_en) && (
            <p className="text-cyan-100 text-sm mt-2 max-w-md">
              {getTranslatedText(formTemplate?.description, formTemplate?.description_en)}
            </p>
          )}

          {aiConversationEnabled && (
            <button
              onClick={() => setUseConversationMode(true)}
              className="mt-4 px-5 py-3 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all hover:scale-105 hover:shadow-lg"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%)',
                color: '#7c3aed',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.8)'
              }}
            >
              <MessageSquare size={18} style={{ color: '#7c3aed' }} />
              <span>
                {formLang === 'en' ? '✨ Try AI conversation' : '✨ Essayer la conversation IA'}
              </span>
            </button>
          )}
        </div>

        <div className="mt-5 space-y-2 relative z-10">
          <div className="flex items-center gap-3">
            <div
              className="flex-1 h-2.5 rounded-full overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.25)' }}
            >
              <div
                className="h-full rounded-full transition-all duration-700 ease-out"
                style={{
                  width: `${((currentCardIndex + 1) / totalCards) * 100}%`,
                  background: 'linear-gradient(90deg, #ffffff 0%, #e0f2fe 100%)'
                }}
              />
            </div>
            <span
              className="text-sm font-bold min-w-[50px] text-right px-2 py-1 rounded-lg"
              style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}
            >
              {Math.round(((currentCardIndex + 1) / totalCards) * 100)}%
            </span>
          </div>

          {showProgressInfo && (
            <div className="flex items-center justify-between text-xs text-cyan-100">
              <span className="font-medium">
                {formLang === 'en' ? `Step ${currentCardIndex + 1} of ${totalCards}` : `Étape ${currentCardIndex + 1} sur ${totalCards}`}
              </span>
              {remainingTime > 0 && !isLastCard && (
                <span className="flex items-center gap-1.5 px-2 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.1)' }}>
                  <Clock size={12} />
                  {formLang === 'en' ? `~${formatRemainingTime(remainingTime)} left` : `~${formatRemainingTime(remainingTime)} restant`}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div
        className={`${embedded ? 'p-5' : 'flex-1 overflow-y-auto p-6'} transition-all duration-300 ${getAnimationClasses()}`}
        style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}
      >
        {currentCard && (
          <>
            {showImportantBadge && currentCard.isImportant && (
              <div
                className="mb-4 px-4 py-3 rounded-xl flex items-center gap-3"
                style={{
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                  border: '1px solid #fbbf24'
                }}
              >
                <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <AlertTriangle size={16} className="text-white" />
                </div>
                <span className="text-sm font-semibold text-amber-800">
                  {formLang === 'en' ? 'Important step - Please read carefully' : 'Étape importante - Veuillez lire attentivement'}
                </span>
              </div>
            )}

            <div className="mb-6">
              <h3
                className={`${customStyles.headingSize} ${customStyles.headingWeight} mb-1`}
                style={{ color: customStyles.headingColor, fontFamily: customStyles.fontFamily }}
              >
                {getTranslatedText(currentCard.title, currentCard.title_en)}
              </h3>
              {currentCard.description && (
                <p className="text-sm text-slate-500">
                  {getTranslatedText(currentCard.description, currentCard.description_en)}
                </p>
              )}
            </div>

            <div className="space-y-5">
              {currentCard.fields?.filter(field => !shouldHideField(field)).map((field) => (
                <div key={field.id} className="group">
                  {field.type !== 'heading' && (
                    <label
                      className={`block ${customStyles.labelSize} ${customStyles.labelWeight} mb-2`}
                      style={{ color: customStyles.labelColor, fontFamily: customStyles.fontFamily }}
                    >
                      {getTranslatedText(field.label, field.label_en)}
                      {field.validation?.required && (
                        <span className="text-rose-500 ml-1">*</span>
                      )}
                    </label>
                  )}
                  <div className="relative">
                    <DynamicFormField field={field} form={form} />
                  </div>
                  {(field.description || field.description_en) && (
                    <p className="text-xs text-slate-400 mt-2 flex items-start gap-1.5">
                      <HelpCircle size={12} className="flex-shrink-0 mt-0.5 text-slate-400" />
                      <span>{getTranslatedText(field.description, field.description_en)}</span>
                    </p>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div
        className={`p-4 flex justify-between items-center gap-3 ${embedded ? 'rounded-b-2xl' : ''}`}
        style={{
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          borderTop: '1px solid #e2e8f0'
        }}
      >
        <button
          onClick={handlePrevious}
          disabled={currentCardIndex === 0}
          className="px-5 py-3 rounded-xl disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 font-medium transition-all hover:scale-105"
          style={{
            background: currentCardIndex === 0 ? '#f1f5f9' : '#ffffff',
            color: '#475569',
            border: '1px solid #e2e8f0',
            boxShadow: currentCardIndex === 0 ? 'none' : '0 2px 8px rgba(0,0,0,0.05)'
          }}
        >
          <ChevronLeft size={18} />
          <span className="hidden sm:inline">{t('form.previous')}</span>
        </button>

        {isLastCard ? (
          <button
            onClick={() => {
              if (showRecapBeforeSend) {
                setShowRecap(true);
              } else {
                handleSubmit();
              }
            }}
            disabled={!isCurrentCardValid() || submitting}
            className="px-6 py-3 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-semibold transition-all hover:scale-105"
            style={{
              background: `linear-gradient(135deg, ${customStyles.accentColor} 0%, ${customStyles.accentColor}dd 100%)`,
              boxShadow: `0 4px 15px ${customStyles.accentColor}55`
            }}
          >
            {submitting ? (
              <>
                <RefreshCw className="animate-spin" size={18} />
                {t('form.sending')}
              </>
            ) : showRecapBeforeSend ? (
              <>
                <CheckCircle size={18} />
                {formLang === 'en' ? 'Review & Send' : 'Vérifier et Envoyer'}
              </>
            ) : (
              <>
                <Send size={18} />
                {t('form.send')}
              </>
            )}
          </button>
        ) : (
          <button
            onClick={handleNext}
            disabled={!isCurrentCardValid()}
            className="px-6 py-3 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-semibold transition-all hover:scale-105"
            style={{
              background: `linear-gradient(135deg, ${customStyles.accentColor} 0%, ${customStyles.accentColor}dd 100%)`,
              boxShadow: `0 4px 15px ${customStyles.accentColor}55`
            }}
          >
            {t('form.next')}
            <ChevronRight size={18} />
          </button>
        )}
      </div>
    </>
  );

  if (embedded) {
    return (
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}
      >
        {formContent}
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ background: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className="bg-white w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col"
        style={{
          borderRadius: '1.5rem',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
      >
        {formContent}
      </div>
    </div>
  );
};

export default DynamicFormModal;
